'use client'

import { integerToMoney } from '@/app/lib/moneyHandler'
import { CohortDurationBand } from '@/types/search'
import { Close as CloseIcon } from '@mui/icons-material'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { Fragment } from 'react'
import {
  useCurrentRefinements,
  UseCurrentRefinementsProps,
} from 'react-instantsearch'

type CustomCurrentRefinementsProps = UseCurrentRefinementsProps & {
  className?: string
}

export function CustomCurrentRefinements({
  className,
  ...currentRefinementsProps
}: CustomCurrentRefinementsProps) {
  const t = useTranslations('SearchRefinements')
  const tAria = useTranslations('CustomCurrentRefinements')
  const { items, refine } = useCurrentRefinements(currentRefinementsProps)

  if (items.length === 0) {
    return null
  }

  const itemsToShow = items.filter((item) => item.attribute !== 'tags.slug')

  return (
    <div className={cn('flex w-full flex-wrap items-start gap-2', className)}>
      {itemsToShow.map((item) => (
        <Fragment key={item.indexName}>
          {item.refinements.map((refinement) => (
            <button
              key={`${refinement.attribute}-${refinement.value}`}
              type="button"
              onClick={(event) => {
                if (isModifierClick(event)) {
                  return
                }
                refine(refinement)
              }}
              className={cn(
                'flex items-center gap-2 px-2 py-2 text-left',
                'border border-solid border-lines bg-grey-background',
                'rounded-[4px]',
                'transition-colors hover:bg-mydra-gray',
                'focus:outline-none focus:ring-2 focus:ring-mydra-black focus:ring-opacity-50'
              )}
              aria-label={tAria('remove_filter_aria_label', {
                label: refinement.label,
              })}
            >
              <span className="text-sm font-normal leading-[14.982px] text-mydra-black">
                {refinement.attribute === 'duration.band'
                  ? t(
                      `course_duration.${refinement.value as Exclude<NonNullable<CohortDurationBand>, undefined>}`
                    )
                  : refinement.attribute === 'prices.amount'
                    ? `${refinement.operator} ${integerToMoney(refinement.value)}`
                    : refinement.label}
              </span>
              <CloseIcon className="text-mydra-black" sx={{ fontSize: 12 }} />
            </button>
          ))}
        </Fragment>
      ))}
    </div>
  )
}

function isModifierClick(event: React.MouseEvent) {
  const isMiddleClick = event.button === 1

  return Boolean(
    isMiddleClick ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
  )
}
